import { useEffect, useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Rocket, Coins, Gavel, TrendingUp, Sparkles, RefreshCcw, Send, Award } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';

export default function NextLevelHub() {
    // Points
    const [points, setPoints] = useState({ balance: 0, history: [], rate: '' });
    const [redeem, setRedeem] = useState(0);
    // Boost
    const [plans, setPlans] = useState([]);
    const [myAds, setMyAds] = useState([]);
    const [boostAd, setBoostAd] = useState(null);
    const [boostPlan, setBoostPlan] = useState('7d');
    // Disputes
    const [disputes, setDisputes] = useState([]);
    const [newDispute, setNewDispute] = useState(null);
    const [reason, setReason] = useState('');
    const [orders, setOrders] = useState([]);
    // Market stats
    const [statsCat, setStatsCat] = useState('electronica');
    const [stats, setStats] = useState(null);
    // Recommendations
    const [recs, setRecs] = useState([]);

    const load = async () => {
        try {
            const [p, pl, ads, d, o, r] = await Promise.all([
                api.get('/me/points'),
                api.get('/boost/plans'),
                api.get('/me/ads').catch(() => ({ data: [] })),
                api.get('/me/disputes'),
                api.get('/me/orders/purchases'),
                api.get('/recommendations?limit=8'),
            ]);
            setPoints(p.data); setPlans(pl.data.plans); setMyAds(ads.data); setDisputes(d.data); setOrders(o.data); setRecs(r.data.items || []);
        } catch (e) { console.error(e); }
    };
    useEffect(() => { load(); }, []);

    const loadStats = async (cat) => {
        try { const { data } = await api.get(`/market/stats?category_slug=${cat}&days=90`); setStats(data); }
        catch { setStats(null); }
    };
    useEffect(() => { loadStats(statsCat); }, [statsCat]);

    const doRedeem = async () => {
        try { await api.post('/me/points/redeem', { amount: Number(redeem) }); toast.success('Puntos canjeados'); setRedeem(0); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const doBoost = async () => {
        if (!boostAd) return;
        try { const { data } = await api.post('/boost/purchase', { ad_id: boostAd.id, plan: boostPlan }); toast.success(`Boost activo hasta ${new Date(data.boosted_until).toLocaleDateString()}`); setBoostAd(null); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const createDispute = async () => {
        if (!newDispute || reason.length < 5) { toast.error('Motivo mínimo 5 caracteres'); return; }
        try { await api.post('/disputes', { order_id: newDispute.id, reason }); toast.success('Disputa abierta — el equipo mediará'); setNewDispute(null); setReason(''); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const eligibleForDispute = orders.filter(o => ['paid', 'delivered', 'released'].includes(o.status) && !disputes.some(d => d.order_id === o.id && d.status !== 'resolved'));

    return (
        <div className="space-y-5" data-testid="next-level-hub">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight flex items-center gap-2"><Award className="h-5 w-5 text-red-600" />Beneficios</h1>
                <Button variant="outline" size="sm" onClick={load}><RefreshCcw className="h-4 w-4 mr-1" />Actualizar</Button>
            </div>

            <Tabs defaultValue="points">
                <TabsList>
                    <TabsTrigger value="points" data-testid="tab-points"><Coins className="h-3.5 w-3.5 mr-1" />Puntos OK</TabsTrigger>
                    <TabsTrigger value="boost" data-testid="tab-boost"><Rocket className="h-3.5 w-3.5 mr-1" />Destacar anuncios</TabsTrigger>
                    <TabsTrigger value="disputes" data-testid="tab-disputes"><Gavel className="h-3.5 w-3.5 mr-1" />Disputas</TabsTrigger>
                    <TabsTrigger value="market" data-testid="tab-market"><TrendingUp className="h-3.5 w-3.5 mr-1" />Precios de mercado</TabsTrigger>
                    <TabsTrigger value="recs" data-testid="tab-recs"><Sparkles className="h-3.5 w-3.5 mr-1" />Recomendaciones</TabsTrigger>
                </TabsList>

                {/* PUNTOS */}
                <TabsContent value="points" className="mt-4 space-y-4">
                    <div className="rounded-2xl border bg-gradient-to-br from-amber-50 via-white to-red-50 p-5">
                        <div className="text-xs uppercase text-muted-foreground">Saldo actual</div>
                        <div className="text-5xl font-bold text-red-600 tabular-nums" data-testid="points-balance">{points.balance} pts</div>
                        <div className="text-sm text-muted-foreground">≈ {formatMoney(points.balance / 10, 'MXN')} · {points.rate}</div>
                        <div className="mt-3 flex gap-2 items-end">
                            <div className="flex-1"><label className="text-xs">Canjear puntos</label><Input type="number" value={redeem} onChange={(e) => setRedeem(e.target.value)} data-testid="points-redeem-input" /></div>
                            <Button onClick={doRedeem} disabled={!redeem || redeem <= 0} className="bg-red-600 hover:bg-red-700" data-testid="points-redeem-btn">Canjear</Button>
                        </div>
                    </div>
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="font-semibold text-sm mb-2">Historial</div>
                        {points.history.length === 0 ? <div className="text-sm text-muted-foreground">Aún no hay movimientos. Gana <b>2% en puntos</b> por cada compra completada.</div> :
                            points.history.map(t => (
                                <div key={t.id} className="flex justify-between border-b py-1.5 text-sm">
                                    <span className="text-muted-foreground">{new Date(t.created_at).toLocaleDateString('es-MX')} · {t.kind}</span>
                                    <span className={t.delta >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{t.delta > 0 ? '+' : ''}{t.delta} pts</span>
                                </div>
                            ))
                        }
                    </div>
                </TabsContent>

                {/* BOOST */}
                <TabsContent value="boost" className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {plans.map(p => (
                            <div key={p.key} className="rounded-2xl border-2 border-red-100 bg-card p-4" data-testid={`plan-${p.key}`}>
                                <div className="text-xs uppercase text-red-600 font-bold">{p.key}</div>
                                <div className="text-3xl font-bold text-neutral-900">{formatMoney(p.price_mxn, 'MXN')}</div>
                                <div className="text-sm text-muted-foreground">{p.days} día{p.days > 1 ? 's' : ''} destacado</div>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="font-semibold text-sm mb-2">Mis anuncios</div>
                        {myAds.length === 0 ? <div className="text-sm text-muted-foreground">No tienes anuncios publicados.</div> :
                            <div className="space-y-2">
                                {myAds.slice(0, 20).map(a => (
                                    <div key={a.id} className="flex items-center gap-3 border rounded p-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">{a.title}</div>
                                            <div className="text-xs text-muted-foreground">{formatMoney(a.price, 'MXN')} {a.boosted && <Badge className="ml-1 bg-red-600">🚀 Boost activo</Badge>}</div>
                                        </div>
                                        <Button size="sm" variant="outline" onClick={() => setBoostAd(a)} data-testid={`boost-btn-${a.id}`}><Rocket className="h-4 w-4 mr-1" />Destacar</Button>
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                </TabsContent>

                {/* DISPUTES */}
                <TabsContent value="disputes" className="mt-4 space-y-3">
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="font-semibold text-sm mb-2">Abrir nueva disputa</div>
                        {eligibleForDispute.length === 0 ? <div className="text-sm text-muted-foreground">Sin órdenes elegibles.</div> :
                            <div className="space-y-2">
                                {eligibleForDispute.slice(0, 10).map(o => (
                                    <div key={o.id} className="flex items-center justify-between border rounded p-2">
                                        <div><div className="text-sm font-medium truncate">{o.ad_snapshot?.title}</div><div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div></div>
                                        <Button size="sm" variant="outline" onClick={() => setNewDispute(o)} data-testid={`dispute-open-${o.id}`}><Gavel className="h-4 w-4 mr-1" />Abrir disputa</Button>
                                    </div>
                                ))}
                            </div>
                        }
                    </div>
                    <div className="rounded-2xl border bg-card p-4">
                        <div className="font-semibold text-sm mb-2">Mis disputas</div>
                        {disputes.length === 0 ? <div className="text-sm text-muted-foreground">Sin disputas.</div> :
                            disputes.map(d => (
                                <div key={d.id} className="border rounded p-3 mb-2" data-testid={`dispute-${d.id}`}>
                                    <div className="flex items-center gap-2"><Badge variant={d.status === 'resolved' ? 'default' : 'outline'} className={d.status === 'resolved' ? 'bg-emerald-600' : ''}>{d.status}</Badge>{d.resolution && <Badge variant="outline">{d.resolution}</Badge>}<span className="text-xs text-muted-foreground ml-auto">{new Date(d.created_at).toLocaleDateString('es-MX')}</span></div>
                                    <div className="text-sm mt-2"><b>Motivo:</b> {d.reason}</div>
                                    {d.admin_note && <div className="text-xs text-muted-foreground mt-1"><b>Admin:</b> {d.admin_note}</div>}
                                </div>
                            ))
                        }
                    </div>
                </TabsContent>

                {/* MARKET */}
                <TabsContent value="market" className="mt-4 space-y-4">
                    <div className="rounded-2xl border bg-card p-4 space-y-3">
                        <div className="flex gap-2 items-end">
                            <div className="flex-1"><label className="text-xs">Categoría</label>
                                <Select value={statsCat} onValueChange={setStatsCat}>
                                    <SelectTrigger data-testid="market-cat"><SelectValue /></SelectTrigger>
                                    <SelectContent>{['electronica', 'ropa', 'calzado', 'muebles', 'juguetes', 'belleza', 'deportes', 'hogar', 'vehiculos', 'libros'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        {stats && (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <Stat label="Promedio" v={formatMoney(stats.avg, 'MXN')} />
                                    <Stat label="Mediana" v={formatMoney(stats.median, 'MXN')} />
                                    <Stat label="Mín / Máx" v={`${formatMoney(stats.min, 'MXN')} - ${formatMoney(stats.max, 'MXN')}`} />
                                    <Stat label="Anuncios" v={stats.count} />
                                </div>
                                {stats.trend?.length > 0 && (
                                    <div className="h-48">
                                        <ResponsiveContainer><LineChart data={stats.trend}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="period" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Line type="monotone" dataKey="avg" stroke="#dc2626" strokeWidth={2} /></LineChart></ResponsiveContainer>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </TabsContent>

                {/* RECS */}
                <TabsContent value="recs" className="mt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {recs.map(r => (
                            <a key={r.id} href={`/ad/${r.id}`} className="rounded-xl border bg-card overflow-hidden hover:shadow" data-testid={`rec-${r.id}`}>
                                <div className="aspect-square bg-neutral-100">{r.media?.[0]?.url && <img src={r.media[0].url.startsWith('http') ? r.media[0].url : `${(process.env.REACT_APP_BACKEND_URL || '')}${r.media[0].url}`} alt="" className="w-full h-full object-cover" />}</div>
                                <div className="p-2"><div className="text-xs font-semibold truncate">{r.title}</div><div className="text-xs text-red-600 font-bold">{formatMoney(r.price, 'MXN')}</div></div>
                            </a>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Boost dialog */}
            <Dialog open={!!boostAd} onOpenChange={() => setBoostAd(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader><DialogTitle>Destacar anuncio</DialogTitle><DialogDescription>{boostAd?.title}</DialogDescription></DialogHeader>
                    <div className="space-y-2">
                        {plans.map(p => (
                            <button key={p.key} onClick={() => setBoostPlan(p.key)} className={`w-full flex items-center justify-between rounded-xl border-2 p-3 ${boostPlan === p.key ? 'border-red-600 bg-red-50' : 'border-neutral-200'}`} data-testid={`select-plan-${p.key}`}>
                                <span className="font-bold uppercase">{p.key}</span>
                                <span className="text-red-600 font-bold">{formatMoney(p.price_mxn, 'MXN')}</span>
                            </button>
                        ))}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setBoostAd(null)}>Cancelar</Button><Button onClick={doBoost} className="bg-red-600 hover:bg-red-700" data-testid="confirm-boost">Activar destacado</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dispute dialog */}
            <Dialog open={!!newDispute} onOpenChange={() => setNewDispute(null)}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md">
                    <DialogHeader><DialogTitle>Abrir disputa</DialogTitle><DialogDescription>{newDispute?.ad_snapshot?.title}</DialogDescription></DialogHeader>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={5} placeholder="Describe el problema con detalle…" data-testid="dispute-reason" />
                    <DialogFooter><Button variant="outline" onClick={() => setNewDispute(null)}>Cancelar</Button><Button onClick={createDispute} className="bg-red-600 hover:bg-red-700" data-testid="submit-dispute"><Send className="h-4 w-4 mr-1" />Enviar</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, v }) { return <div className="rounded-lg border p-2 bg-secondary/40"><div className="text-[10px] text-muted-foreground">{label}</div><div className="font-semibold text-sm">{v}</div></div>; }
